import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpenIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

// Layout
import LayoutWithOutNavBar from '../../components/layout/LayoutWithOutNavBar';

// Services
import { resendVerificationEmail } from '../../services/userAPIService';
import { AuthContext } from '../../context/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [verificationError, setVerificationError] = useState(false);
  const [isVerificationSent, setIsVerificationSent] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setVerificationError(false);
    setIsVerificationSent(false);
    setIsLoading(true);

    try {
      const response = await login({ email, password });
      if (response.status === 200) {
        navigate('/home');
      }
    } catch (error) {
      console.log("LoginPage error:", error);
      if (error.code === 'EMAIL_NOT_VERIFIED') {
        setVerificationError(true);
        setError(error.message);
      } else if (error.code === 'AUTH_ERROR') {
        setError(error.message);
      } else {
        setError('Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es später erneut.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    setError('');
    try {
      await resendVerificationEmail(email);
      setIsVerificationSent(true);
      setVerificationError(false);
    } catch (error) {
      setError('Fehler beim Senden der Verifizierungsmail. Bitte versuche es später erneut.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <LayoutWithOutNavBar>
      <div className="min-h-screen bg-base-200">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <Link
            to="/"
            className="btn btn-ghost btn-sm gap-2 mb-8 hover:bg-base-300"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Zurück
          </Link>
          
          <div className="flex flex-col items-center justify-center">
            {/* Branding Section */}
            <div className="text-center mb-8">
              <BookOpenIcon className="w-12 h-12 text-primary mx-auto mb-4" />
              <h1 className="text-3xl font-bold">
                Mein-SKS
              </h1>
            </div>

            {/* Login Card */}
            <div className="card w-full max-w-md bg-base-100 shadow-lg">
              <div className="card-body space-y-6">
                <h2 className="text-xl font-bold text-center">
                  Anmelden
                </h2>

                {error && (
                  <div className="alert alert-error">
                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}

                {verificationError && (
                  <button
                    type="button"
                    className="btn btn-error w-full"
                    onClick={handleResendVerification}
                    disabled={isResending}
                  >
                    {isResending ? (
                      <>
                        <span className="loading loading-spinner"></span>
                        <span>Sende...</span>
                      </>
                    ) : 'Verifizierungsmail erneut senden'}
                  </button>
                )}

                {isVerificationSent && (
                  <div className="alert alert-success">
                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Verifizierungsmail wurde erneut gesendet.</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">E-Mail-Adresse</span>
                    </label>
                    <input
                      type="email"
                      placeholder="segler@beispiel.de"
                      className="input input-bordered bg-base-200 focus:bg-base-100"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">Passwort</span>
                    </label>
                    <input
                      type="password"
                      placeholder="••••••••"
                      className="input input-bordered bg-base-200 focus:bg-base-100"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <label className="label">
                      <Link to="/password-reset" className="label-text-alt link link-primary hover:link-hover">
                        Passwort vergessen?
                      </Link>
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-full"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="loading loading-spinner"></span>
                        <span>Leinen los...</span>
                      </>
                    ) : 'Einloggen'}
                  </button>

                  <div className="divider text-base-content/60">ODER</div>

                  <div className="text-center space-y-2">
                    <p className="text-base-content/70">Noch nicht an Bord?</p>
                    <Link
                      to="/register"
                      className="btn btn-outline btn-secondary w-full group hover:scale-[1.02] transition-all duration-200"
                    >
                      Jetzt kostenlos registrieren
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutWithOutNavBar>
  );
};

export default LoginPage;
